import type { UserActivationBrowserOpenOptions } from '@hibro/shared';

const CHANNEL_PREFIX = 'hibro:browser-window:';
const WINDOW_NAME = 'hibro-browser';
const HANDOFF_TIMEOUT_MS = 15_000;

interface HandoffChannel {
  onMessage(listener: (data: unknown) => void): void;
  postMessage(message: unknown): void;
  close(): void;
}

interface OpenedWindow {
  focus(): void;
}

export interface BrowserWindowHandoffRuntime {
  origin: string;
  createRequestId(): string;
  createChannel(name: string): HandoffChannel;
  openWindow(url: string, name: string, features: string): OpenedWindow | null;
  setTimer(callback: () => void, delay: number): unknown;
  clearTimer(timer: unknown): void;
  onError(error: unknown): void;
}

export interface BrowserWindowHandoff {
  open(
    url: string,
    options?: Pick<UserActivationBrowserOpenOptions, 'width' | 'height'>,
  ): boolean;
  receive(
    requestId: string,
    onUrl: (url: string) => void | Promise<void>,
  ): () => void;
  dispose(): void;
}

function defaultRuntime(
  onError: (error: unknown) => void,
): BrowserWindowHandoffRuntime {
  return {
    origin: window.location.origin,
    createRequestId: () =>
      globalThis.crypto?.randomUUID?.() ?? String(Math.random()).slice(2),
    createChannel: (name) => {
      const channel = new BroadcastChannel(name);
      return {
        onMessage: (listener) => {
          channel.onmessage = (event) => listener(event.data);
        },
        postMessage: (message) => channel.postMessage(message),
        close: () => channel.close(),
      };
    },
    openWindow: (url, name, features) => window.open(url, name, features),
    setTimer: (callback, delay) => window.setTimeout(callback, delay),
    clearTimer: (timer) => window.clearTimeout(timer as number),
    onError,
  };
}

export function createBrowserWindowHandoff(options?: {
  onError?: (error: unknown) => void;
  runtime?: BrowserWindowHandoffRuntime;
}): BrowserWindowHandoff {
  const runtime =
    options?.runtime ?? defaultRuntime(options?.onError ?? (() => {}));
  const active = new Set<() => void>();

  const track = (channel: HandoffChannel): (() => void) => {
    let disposed = false;
    const dispose = (): void => {
      if (disposed) {
        return;
      }
      disposed = true;
      runtime.clearTimer(timer);
      channel.close();
      active.delete(dispose);
    };
    const timer = runtime.setTimer(dispose, HANDOFF_TIMEOUT_MS);
    active.add(dispose);
    return dispose;
  };

  return {
    open(url, windowOptions) {
      const requestId = runtime.createRequestId();
      const channel = runtime.createChannel(`${CHANNEL_PREFIX}${requestId}`);
      const dispose = track(channel);
      channel.onMessage((data) => {
        const message = data as { type?: unknown } | null;
        if (message?.type === 'hibro:browser-window-ready') {
          channel.postMessage({ type: 'hibro:browser-window-open', url });
        } else if (message?.type === 'hibro:browser-window-opened') {
          dispose();
        }
      });

      const target = new URL(runtime.origin);
      target.searchParams.set('shell', 'browser');
      target.searchParams.set('browserRequest', requestId);
      const opened = runtime.openWindow(
        target.toString(),
        WINDOW_NAME,
        `popup,width=${windowOptions?.width ?? 1100},height=${windowOptions?.height ?? 760},resizable=yes`,
      );
      if (!opened) {
        dispose();
        return false;
      }
      opened.focus();
      return true;
    },
    receive(requestId, onUrl) {
      const channel = runtime.createChannel(`${CHANNEL_PREFIX}${requestId}`);
      const dispose = track(channel);
      channel.onMessage((data) => {
        const message = data as {
          type?: unknown;
          url?: unknown;
        } | null;
        if (
          message?.type !== 'hibro:browser-window-open' ||
          typeof message.url !== 'string'
        ) {
          return;
        }
        void Promise.resolve(onUrl(message.url))
          .then(() => {
            channel.postMessage({ type: 'hibro:browser-window-opened' });
            dispose();
          })
          .catch((error: unknown) => {
            runtime.onError(error);
            dispose();
          });
      });
      channel.postMessage({ type: 'hibro:browser-window-ready' });
      return dispose;
    },
    dispose() {
      for (const dispose of [...active]) {
        dispose();
      }
    },
  };
}
